import FilterableArticleGrid from '@/components/blog/FilterableArticleGrid';
import { FilterBlock_Category } from '@/components/FilterBlock';
import Image from '@/components/Image';
import { Container } from '@/components/layout/Container';
import Link from '@/components/Link';
import { GlobalContext } from '@/context';
import {
  BlogCategoryCountQuery,
  BlogCategoryCountQueryVariables,
  BlogCategoryPathsQuery,
  BlogCategoryQuery,
  BlogCategoryQueryVariables,
} from '@/graphql';
import BlogCategoryQueryDocument from '@/graphql/queries/BlogCategory.gql';
import BlogCategoryCountQueryDocument from '@/graphql/queries/BlogCategoryCount.gql';
import BlogCategoryPathsQueryDocument from '@/graphql/queries/BlogCategoryPaths.gql';
import { getPageProps } from '@/lib/craft-page';
import CraftClient from '@/lib/graphql';
import gtm from '@/lib/gtm';
import useBreakpoint from '@/lib/hooks/useBreakpoint';
import { getStaticPagePaths, getStaticPageProps } from '@/lib/next';
import { maybeNotSet } from '@/lib/typescript-helpers';
import { COLORS } from '@/styles/color';
import { hexToRGBA } from '@/styles/color/utils';
import { mediaBreakpointDown, mediaBreakpointUp, paddingX, paddingY } from '@/styles/layout/utils';
import { FONT_FAMILIES, FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import BlogTemplate from '@/templates/BlogTemplate';
import axios, { AxiosResponse } from 'axios';
import { capitalize } from 'lodash';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useContext, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

const ARTICLES_PER_PAGE = 6;
const BORDER_COLOR = hexToRGBA(COLORS.GRAY.BASE, 0.5);

/**
 * Removes a trailing pagination segment (e.g. "/2", "/12") from a URL path so
 * paginated category pages collapse to a single canonical URL.
 *
 *   /blog/category/workflows      -> /blog/category/workflows
 *   /blog/category/workflows/2    -> /blog/category/workflows
 */
const stripPaginationSegment = (path: string): string =>
  path.replace(/\/\d+(?=$|[?#])/, '');

const Header = styled.header`
  position: relative;
  padding-bottom: ${remCalc(20)};

  ${mediaBreakpointUp('desktop')} {
    padding-top: ${remCalc(40)};
  }
`;

const HeroImageWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: ${remCalc(320)};
  min-height: ${remCalc(300)};
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${mediaBreakpointUp('desktop')} {
    max-width: ${remCalc(480)};
  }

  img {
    width: 100%;
    user-drag: none;
    user-select: none;

    ${mediaBreakpointDown('tablet')} {
      transform: rotate(180deg);
    }
  }
`;

const CategoryTitle = styled.h1`
  ${customTextSize(34, 30)};
  font-family: ${FONT_FAMILIES.SERIF};
  text-align: center;
  z-index: 1;
  ${paddingX(40)}

  position: absolute;
  align-self: center;

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(56, 50)};
    ${paddingX(80)}
  }
`;

const FeaturedArticleGrid = styled.div`
  display: grid;
  grid-auto-rows: auto;

  ${mediaBreakpointUp('desktop')} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    row-gap: ${remCalc(64)};
    border-bottom: 1px solid ${BORDER_COLOR};
    margin-bottom: ${remCalc(72)};

    *:first-child {
      grid-column: 1 / span 2;
    }
  }
`;

const EyebrowHeadline = styled.p`
  ${customTextSize(12, 15)};
  font-weight: ${FONT_WEIGHTS.BOLD};
  text-transform: uppercase;
  letter-spacing: 0.1em;

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(13, 16)};
  }
`;

const TitleText = styled.h2`
  font-family: ${FONT_FAMILIES.SERIF};
`;

const ArticleSummary = styled.p`
  ${mediaBreakpointDown('tablet')} {
    display: none;
  }
`;

const ReadMoreLink = styled.a`
  ${customTextSize(16, 16)};
  display: inline-block;
  align-self: flex-start;
  color: ${COLORS.RED.LOWLIGHT};
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  border-bottom: 2px solid ${COLORS.RED.LOWLIGHT};
  margin-top: auto;
  padding-bottom: 2px;
`;

const FocalPointImageLink = styled.a``;

const StyledFocalPointImage = styled.div<{ objectPosition: string }>`
  position: relative;
  border-radius: ${remCalc(10)};
  overflow: hidden;

  img {
    object-fit: cover;
    object-position: ${(props) => props.objectPosition};
  }
`;

const FeaturedArticleCardContent = styled.div`
  display: flex;
  flex-direction: column;
`;

interface FeaturedArticleCardProps {
  first: boolean;
}

const FeaturedArticleCard = styled.div<FeaturedArticleCardProps>`
  display: flex;
  flex-direction: ${(props) => (props.first ? 'column' : 'row')};
  gap: ${remCalc(22)};

  ${mediaBreakpointDown('tablet')} {
    ${(props) =>
    props.first
      ? css`
            margin-bottom: ${remCalc(80)};
          `
      : css`
            ${paddingY(24)}
            border-top: 1px solid ${BORDER_COLOR};

            &:last-child {
              border-bottom: 1px solid ${BORDER_COLOR};
              margin-bottom: ${remCalc(36)};
            }
          `}
  }

  ${mediaBreakpointUp('desktop')} {
    flex-direction: row;
    align-items: ${(props) => (props.first ? 'center' : 'flex-start')};
    gap: ${remCalc(62)};
    padding-bottom: ${remCalc(36)};
    margin-bottom: ${remCalc(36)};

    ${(props) =>
    props.first &&
    css`
        border-bottom: 1px solid ${BORDER_COLOR};
      `}

    &:nth-child(2n) {
      padding-right: ${remCalc(50)};
      border-right: 1px solid ${BORDER_COLOR};
    }

    &:nth-child(2n + 3) {
      padding-left: ${remCalc(50)};
    }
  }

  ${StyledFocalPointImage} {
    ${(props) =>
    props.first
      ? css`
            width: 100%;
            height: ${remCalc(280)};
          `
      : css`
            width: ${remCalc(72)};
            height: ${remCalc(72)};
          `}

    ${mediaBreakpointUp('desktop')} {
      ${(props) =>
    props.first &&
    css`
          width: ${remCalc(472)};
          height: ${remCalc(398)};
        `}
    }
  }

  ${FocalPointImageLink} {
    ${mediaBreakpointUp('desktop')} {
      ${(props) =>
    !props.first &&
    css`
          display: none;
        `}
    }
  }

  ${FeaturedArticleCardContent} {
    gap: ${remCalc(16)};

    ${mediaBreakpointUp('desktop')} {
      gap: ${remCalc(32)};
    }
  }

  ${EyebrowHeadline} {
    ${(props) =>
    props.first &&
    css`
        ${customTextSize(13, 16)};
      `}
  }

  ${TitleText} {
    ${(props) =>
    props.first
      ? css`
            ${customTextSize(26, 30)}
          `
      : css`
            ${customTextSize(15, 19)}
          `}

    ${mediaBreakpointUp('desktop')} {
      ${(props) =>
    props.first
      ? css`
              ${customTextSize(44, 52)};
            `
      : css`
              ${customTextSize(36, 45)}
            `}
    }
  }

  ${ArticleSummary} {
    ${mediaBreakpointUp('desktop')} {
      ${customTextSize(18, 24)};
    }
  }

  ${ReadMoreLink} {
    ${(props) =>
    !props.first &&
    css`
        ${mediaBreakpointDown('tablet')} {
          display: none;
        }
      `}
  }
`;

interface BlogCategoryProps extends BlogCategoryQuery {
  currentPage: number;
}

const BlogCategory: PageTemplate<BlogCategoryProps> = (props) => {
  const router = useRouter();
  const { categories } = useContext(GlobalContext);
  const [, activeBreakpoints] = useBreakpoint();
  const [activeFilterIds, setActiveFilters] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(props.currentPage);
  const [results, setResults] = useState<BlogCategoryQuery>({
    category: props.category,
    entries: props.entries,
    entryCount: props.entryCount,
  });

  const slug = results.category?.slug!;

  const [queryCategories, cacheKey] = useMemo(() => {
    const filters: { id: string[] }[] = [];

    if (activeFilterIds.length) {
      filters.push({
        id: activeFilterIds,
      });
    }

    const cacheKey = activeFilterIds.map((id) => `categoryId=${id}`).join('&');

    return [filters, cacheKey];
  }, [slug, activeFilterIds]);

  useEffect(() => {
    axios
      .post<BlogCategoryQueryVariables, AxiosResponse<BlogCategoryQuery>>('/blog/category/paginate', {
        slug,
        limit: ARTICLES_PER_PAGE,
        offset: (currentPage - 1) * ARTICLES_PER_PAGE,
        categories: queryCategories,
      })
      .then((response) => {
        setResults(response.data);
      })
      .catch((err) => {
        setResults({
          category: props.category,
          entries: props.entries,
          entryCount: props.entryCount,
        });
      })
      .finally(() => {
        setCurrentPage(currentPage);
      });
  }, [currentPage, cacheKey]);

  const { category, entries, entryCount } = results;

  /* Update the current page on navigation */
  useEffect(() => {
    if ('slug' in router.query && Array.isArray(router.query.slug)) {
      const pageNumber = parseInt(router.query.slug[1], 10);

      if (pageNumber && pageNumber !== currentPage) setCurrentPage(pageNumber);
    }
  }, [router.query]);

  /* Update the current page if the user switches pages */
  useEffect(() => {
    if (props.currentPage !== currentPage) {
      setCurrentPage(props.currentPage);
    }
  }, [props.currentPage]);

  /* Update the category when switching to another */
  useEffect(() => {
    setCurrentPage(props.currentPage);
    setResults({
      entryCount: props.entryCount,
      category: props.category,
      entries: props.entries,
    });
  }, [props.category]);

  /* Generate the array of filters */
  const filterCategories = useMemo(() => {
    const sortedFilters = categories?.reduce<FilterBlock_Category[]>((groups, category) => {
      const { id, slug, groupHandle, title } = category!;
      const index = groups.findIndex((group) => group.slug === groupHandle);

      const filter = {
        id: id!,
        title: title!,
        slug: slug!,
      };

      /* Non-empty filters */
      const titleFilter = ['teams', 'industries'];

      if (index > -1) {
        groups[index].filters.push(filter);
      } else {
        if (titleFilter.includes(groupHandle!)) {
          groups.push({
            title: capitalize(groupHandle!),
            slug: groupHandle!,
            filters: [filter],
          });
        }
      }

      return groups;
    }, []);

    return sortedFilters?.filter((group) => group.slug !== 'blog') || [];
  }, [categories]);

  const heroImage = category?.heroBackground?.[0]!;

  if (router.isFallback) return <h1>loading...</h1>;

  const blogCategoryUrl = stripPaginationSegment(
    typeof window !== 'undefined' ? window.location.href : '',
  );

  return (
    <Container>
      <Head>
        <link
          href={blogCategoryUrl}
          rel="canonical"
          key="link-canonical"
        />
      </Head>

      <Header>
        <HeroImageWrapper>
          <CategoryTitle>{category?.title}</CategoryTitle>
          <Image
            src={category?.heroBackground?.[0]?.url!}
            width={heroImage?.width!}
            height={heroImage?.height!}
            alt=""
          />
        </HeroImageWrapper>
      </Header>

      {!!category?.featuredArticles?.length && (
        <FeaturedArticleGrid>
          {category?.featuredArticles?.map((article, index) => {
            const image = article?.coverImage?.[0]!;
            const onClick = () => {
              gtm.track('click', {
                element_label: `Position ${index + 1} | ${maybeNotSet(article?.title)}`,
                location: 'Featured Article Cards',
                element_type: 'link',
              });
            };

            return (
              <FeaturedArticleCard key={article?.id} first={index === 0}>
                <Link href={article?.url ?? '#'}>
                  <FocalPointImageLink onClick={onClick}>
                    <StyledFocalPointImage
                      objectPosition={image.focalPoint?.map((pos) => `${(pos ?? 0.5) * 100}%`).join(' ') ?? 'center'}
                    >
                      <Image src={image.url!} alt="" fill />
                    </StyledFocalPointImage>
                  </FocalPointImageLink>
                </Link>

                <FeaturedArticleCardContent>
                  <EyebrowHeadline>{category.title}</EyebrowHeadline>
                  <Link href={article?.url ?? '#'}>
                    <a onClick={onClick}>
                      <TitleText>{article?.title}</TitleText>
                    </a>
                  </Link>
                  <ArticleSummary>{article?.summary}</ArticleSummary>
                  <Link href={article?.url ?? '#'}>
                    <ReadMoreLink onClick={onClick}>Read more</ReadMoreLink>
                  </Link>
                </FeaturedArticleCardContent>
              </FeaturedArticleCard>
            );
          })}
        </FeaturedArticleGrid>
      )}

      <FilterableArticleGrid
        entries={entries || []}
        filtersCollapsible={!activeBreakpoints.includes('desktop')}
        filterCategories={filterCategories}
        filterSelections={activeFilterIds}
        totalEntries={entryCount}
        entriesPerPage={ARTICLES_PER_PAGE}
        currentPage={currentPage}
        articleCategoryOverride={category?.title!}
        hrefBuilder={(page) => `/blog/category/${category?.slug}/${page}`}
        onFilterToggle={(category, filter, isSelected) => {
          if (isSelected) {
            setActiveFilters([...activeFilterIds, filter.id]);
          } else {
            setActiveFilters(activeFilterIds.filter((id) => id !== filter.id));
          }
        }}
        onPageChange={(page) => {
          setCurrentPage(page);

          router.push(
            {
              pathname: `/blog/category/${category?.slug}/${page}`,
            },
            undefined,
            {
              shallow: true,
            },
          );
        }}
      />
    </Container>
  );
};

BlogCategory.template = BlogTemplate;

export default BlogCategory;

export const getStaticProps = getStaticPageProps<BlogCategoryProps, { slug: string[] }>(
  (params) => `blog/category/${params?.slug.join('/')}`,
  async ({ client, context: { params } }) => {
    // Ensure there are no more than the expected two parameters
    if (!params || params.slug.length > 2) {
      return { notFound: true };
    }

    const [category, page] = params?.slug!;

    // Ensure the second parameter is a number if it exists
    if (!!page && !page.match(/^\d{1,3}$/)) {
      return { notFound: true };
    }

    const currentPage = parseInt(page, 10) || 1;

    const pageProps = await getPageProps<BlogCategoryQuery, BlogCategoryQueryVariables>({
      pathPrefix: '/blog/category',
      queryDocument: BlogCategoryQueryDocument,
      client,
      variables: {
        limit: ARTICLES_PER_PAGE,
        slug: category,
        offset: (currentPage - 1) * ARTICLES_PER_PAGE,
        categories: [
          {
            group: ['blog'],
            slug: [category],
          },
        ],
      },
    });

    if (!pageProps?.category || pageProps?.entryCount === 0 || !pageProps?.entries?.length) {
      return { notFound: true };
    }

    return {
      props: {
        ...pageProps,
        currentPage,
      },
    };
  },
);

export const getStaticPaths = getStaticPagePaths('blog/category', async (context) => {

  const client = new CraftClient();

  const data = await client.request<BlogCategoryPathsQuery>(BlogCategoryPathsQueryDocument);

  const paths =
    (await data.categories?.reduce(async (_paths, category) => {
      const paths = await _paths;

      /* Query how many articles this category has */
      const { count } = await client.request<BlogCategoryCountQuery, BlogCategoryCountQueryVariables>(
        BlogCategoryCountQueryDocument,
        { slug: category?.slug! },
      );

      const pageCount = Math.ceil(count / ARTICLES_PER_PAGE);

      /* Create a path for each page for the category */
      const pages = [...Array(pageCount + 1)].map((_, i) => ({
        params: {
          slug: [category?.slug!, ...(i > 0 ? [String(i)] : [])],
        },
      }));

      return [...paths, ...pages];
    }, Promise.resolve([]))) || [];

  return {
    paths,
    fallback: 'blocking',
  };
});
